import type { CSSProperties } from 'react';
import { vars, spacing } from '@/theme';

/**
 * Geometry of the header's right end (#1307-b, owner Q-355 a / Q-357 a).
 *
 * Kept as plain style objects, not .css.ts, so jsdom tests can pin the numbers
 * that produce the layout (vanilla-extract is mocked there). Pseudo-states
 * (hover, :focus-visible) live in appshell.css.ts.
 *
 * Numeric `spacing.*` (the same scale `vars.spacing` exposes as CSS) so the rendered
 * inline px are observable in jsdom.
 *
 * Rules:
 *  - the VISIBLE right edge of the help icon sits `spacing.lg` (24px) from
 *    the viewport edge, mirroring the logo's 24px on the left, at every width;
 *  - `spacing.sm` (8px) between the last control's visible edge and the
 *    help icon's visible left edge, signed in or out.
 *
 * R3 choice (b): the help link keeps its 35px-wide box (hit area unchanged,
 * 35 x header height) and the empty slack either side of the icon is pulled
 * back with negative/reduced margins, so the geometry is measured to the icon.
 */

export const HELP_LINK_BOX_PX = 35;
export const HELP_ICON_DESKTOP_PX = 26;
export const HELP_ICON_MOBILE_PX = 22;

/** Empty space between the help link box edge and the icon's visible edge. */
export function helpIconSlackPx(iconHeight: number): number {
  return (HELP_LINK_BOX_PX - iconHeight) / 2;
}

/** Outer bar + right section padding: together `vars.spacing.lg` to the help link box edge. */
export const headerBarRightPadding = spacing.md;
export const headerRightSectionPadding = spacing.sm;

/** Help link margins: gap `sm` to the icon's visible edge; box protrudes by its slack. */
export function helpLinkMargins(iconHeight: number): Pick<CSSProperties, 'marginLeft' | 'marginRight'> {
  const slack = helpIconSlackPx(iconHeight);
  return {
    marginLeft: spacing.sm - slack,
    marginRight: -slack,
  };
}

/** V1 pill: same height and fill as the help icon beside it. */
export function signInPillStyle(isMobile: boolean): CSSProperties {
  const height = isMobile ? HELP_ICON_MOBILE_PX : HELP_ICON_DESKTOP_PX;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    height,
    lineHeight: `${height}px`,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: isMobile ? 10 : 12,
    paddingRight: isMobile ? 10 : 12,
    backgroundColor: vars.color.chrome.control,
    color: vars.color.neutral.base,
    fontSize: isMobile ? 12 : 13,
    fontWeight: 700,
    borderRadius: vars.radius.pill,
    whiteSpace: 'nowrap',
    textDecoration: 'none',
  };
}
